// src/pages/Articles.tsx - FastMag Articles Management (Improved)
import BarcodeScannerModal from '@/components/BarcodeScannerModal.tsx';
import Select from '@/components/common/Select.tsx';
import { supabase } from '@/lib/supabase';
import type { Article, Category, Location, Subcategory } from '@/types';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit2,
  Link2,
  MapPin,
  Package,
  Plus,
  Scan,
  Search,
  X
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Extended Article type with joined data
interface ArticleWithRelations extends Article {
  categoria?: Category | null
  sottocategoria?: Subcategory | null
}

export default function Articles() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterSubcategoryId, setFilterSubcategoryId] = useState('');
  const [expandedAlternatives, setExpandedAlternatives] = useState<string | null>(null);
  const [filterCompatibleWith, setFilterCompatibleWith] = useState<{ id: string; code: string } | null>(null);

  // Modals (only for scanner)
  const [showScanModal, setShowScanModal] = useState(false);

  // Fetch articles
  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          categoria:categories(id, name),
          sottocategoria:subcategories(id, name)
        `)
        .order('codice_prodotto');

      if (error) throw error;
      return data;
    }
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  // Fetch subcategories
  const { data: subcategories = [] } = useQuery({
    queryKey: ['subcategories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  // Fetch locations
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('sort_order');

      if (error) throw error;

      // Compute full_path for each location
      return data.map((loc: Location) => ({
        ...loc,
        full_path: getFullPath(loc.id, data)
      })) as Location[];
    }
  });

  // Fetch compatibility counts per article
  const { data: compatCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['articles_compat_counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_compatibilities')
        .select('article_id');

      if (error) throw error;

      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        counts[row.article_id] = (counts[row.article_id] || 0) + 1;
      });
      return counts;
    }
  });

  // Fetch compatible article IDs when filtering by compatibility
  const { data: compatibleIds = [] } = useQuery<string[]>({
    queryKey: ['compatible_ids_filter', filterCompatibleWith?.id],
    queryFn: async () => {
      if (!filterCompatibleWith) return [];
      const { data, error } = await supabase
        .from('article_compatibilities')
        .select('compatible_article_id')
        .eq('article_id', filterCompatibleWith.id);

      if (error) throw error;
      return (data || []).map((row: any) => row.compatible_article_id);
    },
    enabled: !!filterCompatibleWith
  });

  // Fetch compatible alternatives for the expanded article
  const { data: alternatives = [] } = useQuery({
    queryKey: ['article_alternatives', expandedAlternatives],
    queryFn: async () => {
      if (!expandedAlternatives) return [];
      const { data, error } = await supabase
        .from('article_compatibilities')
        .select(`
          id,
          note,
          compatible_article:articles!article_compatibilities_compatible_article_id_fkey(
            id,
            codice_prodotto,
            descrizione_prodotto,
            quantita_magazzino_attuale,
            quantita_minima_magazzino
          )
        `)
        .eq('article_id', expandedAlternatives);

      if (error) throw error;
      return data || [];
    },
    enabled: !!expandedAlternatives
  });

  // Build full_path from parent_id
  const getFullPath = (locationId: string, locationsArray: Location[]): string => {
    const location = locationsArray.find(l => l.id === locationId);
    if (!location) return '';

    const path: string[] = [];
    let current: Location | undefined = location;

    while (current) {
      path.unshift(current.description);
      current = current.parent_id ? locationsArray.find(l => l.id === current!.parent_id) : undefined;
    }

    return path.join(' > ');
  };

  // Get location display for an article
  const getArticleLocationPath = (article: ArticleWithRelations): string | null => {
    if (!article.posizione_magazzino_id) return null;
    return getFullPath(article.posizione_magazzino_id, locations);
  };

  // Filter articles
  const filteredArticles = articles.filter(article => {
    // If filtering by compatibility, only show compatible articles
    if (filterCompatibleWith) {
      return compatibleIds.includes(article.id);
    }

    const query = searchQuery.toLowerCase();
    const matchesSearch = (
      article.codice_prodotto?.toLowerCase().includes(query) ||
      article.descrizione_prodotto?.toLowerCase().includes(query) ||
      article.barcode?.toLowerCase().includes(query)
    );

    const matchesCategory = !filterCategoryId || article.categoria_id === filterCategoryId;
    const matchesSubcategory = !filterSubcategoryId || article.sottocategoria_id === filterSubcategoryId;

    return matchesSearch && matchesCategory && matchesSubcategory;
  });

  // Get filtered subcategories by category (for filters)
  const getFilterSubcategories = (): Subcategory[] => {
    if (!filterCategoryId) return [];
    return subcategories.filter(sub => sub.category_id === filterCategoryId);
  };

  // Handle category filter change
  const handleCategoryFilterChange = (categoryId: string) => {
    setFilterCategoryId(categoryId);
    setFilterSubcategoryId('');
  };

  // Handle scan success - navigate to appropriate page
  const handleScanSuccess = (scannedCode: string) => {
    console.log('🔍 Searching for article with code:', scannedCode);

    // Try to find article by barcode or code
    const foundArticle = articles.find(a => {
      const articleCode = a.codice_prodotto?.toLowerCase().replace(/\s/g, '');
      const articleBarcode = a.barcode?.toLowerCase().replace(/\s/g, '');
      const scannedClean = scannedCode.toLowerCase().replace(/\s/g, '');

      return articleCode === scannedClean || articleBarcode === scannedClean;
    });

    if (foundArticle) {
      console.log('✅ Article found:', foundArticle.codice_prodotto);
      // Navigate to quick adjust page
      navigate('/articles/quick-adjust', {
        state: { article: foundArticle }
      });
    } else {
      console.log('❌ Article not found - opening add page with barcode:', scannedCode);
      // Navigate to add page with scanned barcode (NOT article object!)
      navigate('/articles/add', {
        state: {
          scannedBarcode: scannedCode, // Pass barcode as separate field
          scanNotFound: true
        }
      });
    }
  };

  // Navigation handlers
  const openAddPage = () => {
    navigate('/articles/add');
  };

  const openEditPage = (article: ArticleWithRelations) => {
    navigate('/articles/edit', { state: { article } });
  };

  const isLowStock = (article: ArticleWithRelations): boolean => {
    return (article.quantita_magazzino_attuale || 0) <= (article.quantita_minima_magazzino || 0);
  };

  if (loadingArticles) {
    return (
      <div className="p-4 md:p-8 min-h-screen bg-slate-50">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Articoli</h1>
              <p className="text-slate-600">Gestisci il catalogo articoli</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowScanModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 touch-manipulation"
              >
                <Scan className="w-5 h-5" />
                <span className="hidden sm:inline">Scan</span>
              </button>
              <button
                onClick={openAddPage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 touch-manipulation"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Nuovo Articolo</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per codice, descrizione o barcode..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              value={filterCategoryId}
              onChange={(e) => handleCategoryFilterChange(e.target.value)}
              options={[
                { value: '', label: 'Tutte le categorie' },
                ...categories.map(cat => ({ value: cat.id, label: cat.name }))
              ]}
              placeholder="Filtra per categoria..."
            />
            <Select
              value={filterSubcategoryId}
              onChange={(e) => setFilterSubcategoryId(e.target.value)}
              options={[
                { value: '', label: 'Tutte le sottocategorie' },
                ...getFilterSubcategories().map(sub => ({ value: sub.id, label: sub.name }))
              ]}
              disabled={!filterCategoryId}
              placeholder="Filtra per sottocategoria..."
              emptyMessage="Seleziona prima una categoria"
            />
          </div>
        </div>

        {/* Compatibility filter banner */}
        {filterCompatibleWith && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Link2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm text-blue-800 flex-1">
              Articoli compatibili con <strong>{filterCompatibleWith.code}</strong>
            </span>
            <button
              onClick={() => setFilterCompatibleWith(null)}
              className="p-1 hover:bg-blue-200 rounded transition-colors"
              title="Rimuovi filtro"
            >
              <X className="w-4 h-4 text-blue-600" />
            </button>
          </div>
        )}

        {/* Articles List */}
        <div className="space-y-3">
          {filteredArticles.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                {searchQuery ? 'Nessun articolo trovato' : 'Nessun articolo'}
              </h3>
              {!searchQuery && (
                <button
                  onClick={openAddPage}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Aggiungi Primo Articolo
                </button>
              )}
            </div>
          ) : (
            filteredArticles.map(article => (
              <div
                key={article.id}
                className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow ${isLowStock(article) ? 'border-l-4 border-orange-500' : ''
                  }`}
              >
                <div className="flex items-center justify-between gap-4 p-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Package className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-800 truncate">
                          {article.codice_prodotto}
                        </h3>
                        {isLowStock(article) && (
                          <span className="flex items-center gap-1 text-xs text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full flex-shrink-0">
                            <AlertCircle className="w-3 h-3" />
                            Bassa
                          </span>
                        )}
                        {compatCounts[article.id] > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterCompatibleWith({ id: article.id, code: article.codice_prodotto });
                              setExpandedAlternatives(null);
                            }}
                            className="flex items-center gap-1 text-xs text-blue-700 bg-blue-100 hover:bg-blue-200 px-2 py-0.5 rounded-full flex-shrink-0 transition-colors"
                            title="Mostra articoli compatibili"
                          >
                            <Link2 className="w-3 h-3" />
                            {compatCounts[article.id]}
                          </button>
                        )}
                      </div>
                      {article.descrizione_prodotto && (
                        <p className="text-sm text-slate-600 truncate">
                          {article.descrizione_prodotto}
                        </p>
                      )}
                      {getArticleLocationPath(article) && (
                        <div className="flex items-start gap-1 text-xs text-slate-500 mt-1">
                          <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span className="whitespace-normal break-words leading-tight">
                            {getArticleLocationPath(article)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="hidden sm:flex items-center gap-4 text-sm">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Costo</p>
                        <p className="font-medium text-slate-700">
                          {article.costo_acquisto ? `€ ${parseFloat(article.costo_acquisto).toFixed(2)}` : '-'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Prezzo</p>
                        <p className="font-medium text-slate-700">
                          {article.prezzo_vendita ? `€ ${parseFloat(article.prezzo_vendita).toFixed(2)}` : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Qty</p>
                      <p className="font-bold text-slate-800">{article.quantita_magazzino_attuale}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/articles/quick-adjust', {
                          state: { article }
                        });
                      }}
                      className="p-2 hover:bg-green-100 text-green-600 rounded-lg transition-colors touch-manipulation"
                      title="Regola quantità"
                    >
                      <Package className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openEditPage(article)}
                      className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors touch-manipulation"
                      title="Modifica"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                {/* Low stock + has compatibilities: show alternatives toggle */}
                {isLowStock(article) && compatCounts[article.id] > 0 && (
                  <div className="border-t border-slate-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedAlternatives(
                          expandedAlternatives === article.id ? null : article.id
                        );
                      }}
                      className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <Link2 className="w-3 h-3" />
                      {expandedAlternatives === article.id ? 'Nascondi' : 'Vedi'} alternative disponibili
                      {expandedAlternatives === article.id
                        ? <ChevronUp className="w-3 h-3" />
                        : <ChevronDown className="w-3 h-3" />
                      }
                    </button>
                    {expandedAlternatives === article.id && (
                      <div className="px-3 pb-2 space-y-1">
                        {alternatives.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-1">Caricamento...</p>
                        ) : (
                          alternatives.map((alt: any) => {
                            const inStock = (alt.compatible_article?.quantita_magazzino_attuale || 0) > (alt.compatible_article?.quantita_minima_magazzino || 0);
                            return (
                              <div
                                key={alt.id}
                                className={`flex items-center justify-between p-2 rounded text-xs ${inStock ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'
                                  }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <span className="font-semibold text-slate-800">
                                    {alt.compatible_article?.codice_prodotto || '—'}
                                  </span>
                                  {alt.compatible_article?.descrizione_prodotto && (
                                    <p className="text-slate-500 truncate">{alt.compatible_article.descrizione_prodotto}</p>
                                  )}
                                  {alt.note && (
                                    <p className="text-blue-500 italic">{alt.note}</p>
                                  )}
                                </div>
                                <span className={`px-1.5 py-0.5 rounded font-medium ml-2 flex-shrink-0 ${inStock ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'
                                  }`}>
                                  Qty: {alt.compatible_article?.quantita_magazzino_attuale || 0}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Barcode Scanner Modal */}
        <BarcodeScannerModal
          isOpen={showScanModal}
          onClose={() => setShowScanModal(false)}
          onScanSuccess={handleScanSuccess}
        />
      </div>
    </div>
  );
}